import React, { useCallback, useEffect, useRef, useState } from "react";
import { ignoreAbort } from "../utils/errors";
import { fetchLiveVehiclesForLine as fetchLiveVehiclesForLineApi } from "../api/liveTraffic";

// Minimal shape used by the map overlay UI.
export type VehicleOverlay = {
  id: string;
  lat: number;
  lng: number;
  bearing?: number;
  lineId: string;
};

type UseLiveVehiclesProps = {
  // Google Maps instance to place markers on. If null, no network calls are made.
  map: google.maps.Map | null | undefined;

  // If true, when a strict filtered fetch returns no vehicles we fall back to
  // fetching all vehicles and filter client-side (useful for APIs that return
  // numeric line ids inconsistently).
  allowUnfilteredFallback?: boolean;
};

// Normalize line id text for reliable comparisons.
function normLine(x: any) {
  return String(x ?? "")
    .trim()
    .toLowerCase();
}

type LineLiveState = {
  emptyHits: number;
  unavailable: boolean;
};

// number of hits until livetraffic shows alert (no live-traffic)
const MAX_EMPTY_HITS = 5;

// Hook that manages live vehicle polling, marker lifecycle and an in-memory
// overlay representation per line id.
export function useLiveVehicles({
  map,
  allowUnfilteredFallback = false,
}: UseLiveVehiclesProps) {
  // Public state: overlays keyed by line id (array of VehicleOverlay).
  const [liveOverlayByLine, setLiveOverlayByLine] = useState<
    Record<string, VehicleOverlay[]>
  >({});

  // Internal refs to manage google.maps.Marker instances per line and the
  // interval ids used for polling.
  const liveMarkersByLineRef = useRef<
    Record<string, Record<string, google.maps.Marker>>
  >({});
  const liveIntervalsByLineRef = useRef<Record<string, number>>({});

  // Polling interval in ms (adjusted when window/tab visibility changes).
  const [pollMs, setPollMs] = useState(2000);
  const pollMsRef = useRef(pollMs);
  useEffect(() => {
    pollMsRef.current = pollMs;
  }, [pollMs]);

  // Reduce polling frequency when the document is hidden to save resources.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => setPollMs(document.hidden ? 15000 : 2000);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  //state + ref för line live-status
  const [lineStates, setLineStates] = useState<Record<string, LineLiveState>>(
    {}
  );
  const lineStatesRef = useRef<Record<string, LineLiveState>>({});
  useEffect(() => {
    lineStatesRef.current = lineStates;
  }, [lineStates]);

  const updateLineStateForLine = useCallback(
    (lineId: string, vehiclesLength: number) => {

      setLineStates((prev) => {
        const prevState = prev[lineId] ?? { emptyHits: 0, unavailable: false };
        const isEmpty = vehiclesLength === 0;

        let emptyHits = prevState.emptyHits;
        let unavailable = prevState.unavailable;

        if (isEmpty) {
          emptyHits = prevState.emptyHits + 1;
          if (emptyHits >= MAX_EMPTY_HITS) {
            unavailable = true;
          }
        } else {
          emptyHits = 0;
          unavailable = false;
        }

        return {
          ...prev,
          [lineId]: { emptyHits, unavailable },
        };
      });
    },
    []
  );

  // Fetch live vehicles for a specific line. Uses the provided map for API
  // context and applies optional fallback behavior for inconsistent IDs.
  const fetchLiveVehiclesForLine = useCallback(
    async (lineId: string) => {
      if (!map) return [] as VehicleOverlay[];
      const requested = String(lineId).trim();
      const lidForApi = /^\d+$/.test(requested) ? Number(requested) : requested;

      let vehicles = await fetchLiveVehiclesForLineApi(map, lidForApi);

      // Strict client-side filter that ensures returned vehicles match the
      // requested line id when represented as a normalized string.
      const strictlyFiltered = vehicles.filter(
        (v) => normLine(v.lineId) === normLine(requested)
      );

      // Optional fallback: if strict filtering yields nothing, fetch all and
      // filter client-side (helps with APIs that omit the line parameter).
      if (allowUnfilteredFallback && strictlyFiltered.length === 0) {
        const all = await fetchLiveVehiclesForLineApi(map);
        vehicles = all.filter(
          (v) => normLine(v.lineId) === normLine(requested)
        );
      } else {
        vehicles = strictlyFiltered;
      }

      // Normalize shape to VehicleOverlay.
      return vehicles.map((v) => ({
        id: v.id,
        lat: v.lat,
        lng: v.lng,
        bearing: v.bearing,
        lineId: String(v.lineId ?? requested),
      }));
    },
    [map, allowUnfilteredFallback]
  );

  // Update the in-memory overlay list and remove markers that are no longer
  // present. Marker creation/placement is intentionally omitted here — this
  // function focuses on the overlay state; callers may create markers as needed.
  const upsertLiveMarkers = useCallback(
    (lineId: string, vehicles: VehicleOverlay[]) => {
      if (!map) return;

      const store = (liveMarkersByLineRef.current[lineId] ||= {});
      const nextIds = new Set(vehicles.map((v) => v.id));

      // Remove markers that no longer exist in the latest payload.
      Object.keys(store).forEach((id) => {
        if (!nextIds.has(id)) {
          store[id].setMap(null);
          delete store[id];
        }
      });

      // Update public overlay state for consumers (e.g. UI rendering).
      setLiveOverlayByLine((prev) => ({
        ...prev,
        [lineId]: vehicles,
      }));

      updateLineStateForLine(lineId, vehicles.length);
    },
    [map, updateLineStateForLine]
  );

  // Clear a polling interval for a specific line.
  const clearIntervalForLine = useCallback((lineId: string) => {
    const iid = liveIntervalsByLineRef.current[lineId];
    if (iid) {
      clearInterval(iid);
      delete liveIntervalsByLineRef.current[lineId];
    }
  }, []);

  // Start polling and initial fetch for a given line id. Ensures an initial
  // fit-to-bounds on first paint if the vehicles are outside current view.
  const startLiveForLine = useCallback(
    (lineId: string) => {
      if (!map) return;

      if (!liveMarkersByLineRef.current[lineId]) {
        liveMarkersByLineRef.current[lineId] = {};
      }

      setLineStates((prev) => ({
        ...prev,
        [lineId]: { emptyHits: 0, unavailable: false },
      }));

      let firstPaintDone = false;

      // Initial fetch and paint.
      (async () => {
        const vehicles = await fetchLiveVehiclesForLine(lineId);
        upsertLiveMarkers(lineId, vehicles);
        if (!firstPaintDone && vehicles.length && map) {
          const b = new google.maps.LatLngBounds();
          vehicles.forEach((v) => b.extend({ lat: v.lat, lng: v.lng }));
          const curr = map.getBounds();
          // If vehicles are outside current bounds, fit the map to them with padding.
          if (!curr || !curr.intersects(b)) {
            map.fitBounds(b, 60);
          }
          firstPaintDone = true;
        }
      })().catch(ignoreAbort);

      // Reset any existing interval then create a new polling interval.
      clearIntervalForLine(lineId);
      const createTick = () => {
        return window.setInterval(async () => {
          try {
            const vehicles = await fetchLiveVehiclesForLine(lineId);
            upsertLiveMarkers(lineId, vehicles);
          } catch (e) {
            ignoreAbort(e);
          }
        }, pollMsRef.current);
      };
      liveIntervalsByLineRef.current[lineId] = createTick();
    },
    [map, fetchLiveVehiclesForLine, upsertLiveMarkers, clearIntervalForLine]
  );

  // Stop polling and remove markers for a specific line.
  const stopLiveForLine = useCallback(
    (lineId: string) => {
      clearIntervalForLine(lineId);

      const markers = liveMarkersByLineRef.current[lineId];
      if (markers) {
        Object.values(markers).forEach((m) => m.setMap(null));
        delete liveMarkersByLineRef.current[lineId];
      }

      // Remove overlay entry from public state.
      setLiveOverlayByLine((prev) => {
        const copy = { ...prev };
        delete copy[lineId];
        return copy;
      });

      setLineStates((prev) => {
        const copy = { ...prev };
        delete copy[lineId];
        return copy;
      });
    },
    [clearIntervalForLine]
  );

  // Stop all polling and remove all markers.
  const stopAllLive = useCallback(() => {
    Object.keys(liveIntervalsByLineRef.current).forEach((lineId) =>
      clearIntervalForLine(lineId)
    );
    Object.values(liveMarkersByLineRef.current).forEach((markers) => {
      Object.values(markers).forEach((m) => m.setMap(null));
    });
    liveMarkersByLineRef.current = {};
    setLiveOverlayByLine({});
    setLineStates({});
  }, [clearIntervalForLine]);

  // When poll interval changes, restart active line polls to apply new interval.
  useEffect(() => {
    const activeLineIds = Object.keys(liveIntervalsByLineRef.current);
    if (activeLineIds.length === 0) return;
    activeLineIds.forEach((lineId) => {
      stopLiveForLine(lineId);
      startLiveForLine(lineId);
    });
  }, [pollMs, startLiveForLine, stopLiveForLine]);

  // Cleanup on unmount: stop everything.
  useEffect(() => {
    return () => stopAllLive();
  }, [stopAllLive]);

  const unavailableLineIds = React.useMemo(() => {
    const ids = Object.keys(lineStates).filter(
      (id) => lineStates[id]?.unavailable
    );
    return new Set(ids);
  }, [lineStates]);

  return {
    liveOverlayByLine,
    startLiveForLine,
    stopLiveForLine,
    stopAllLive,
    setPollMs,
    unavailableLineIds,
    lineStates,
  };
}
